import type { NavigationScreenTranslationState } from '@gitbook/app-navigation';
import { PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    ButtonGroup,
    confirm,
    Description,
    Dialog,
    DialogRoot,
    EmptyState,
    EmptyStateIcon,
    Heading,
    useDialogTriggerState,
} from '@gitbook/spine-react';

import { BlockNavigationModal, useSetAnalyticsScreen } from '../../../navigation';
import { TranslationsScreenLayout } from '../TranslationsScreenLayout';
import { createGlossaryMorePalette } from './createGlossaryMorePalette';
import { GlossaryEntryRow } from './GlossaryEntryRow';
import { GlossaryImportDialog } from './GlossaryImportDialog';
import { GlossaryLanguagesButton } from './GlossaryLanguagesButton';
import { GlossaryTableHeader } from './GlossaryTableHeader';
import { useGlossaryViewState } from './state';

export function GlossaryView(props: {
    screen: NavigationScreenTranslationState & { organization: string };
}) {
    const { screen } = props;

    const { editable, entries, hasChanges, actions, displayedLanguages } = useGlossaryViewState(
        screen.organization
    );
    const importDialog = useDialogTriggerState();

    useSetAnalyticsScreen(
        {
            organization: screen.organization,
            screen: 'translations_glossary',
        },
        [screen.organization]
    );

    return (
        <>
            <BlockNavigationModal
                block={hasChanges}
                title="Discard changes?"
                confirmLabel="Discard"
                description="You have unsaved glossary changes. Do you want to leave and discard them? All changes will be lost."
                onConfirm={actions.discardChanges}
            />
            <TranslationsScreenLayout
                screen={screen}
                headerControls={
                    <ButtonGroup>
                        <PaletteButton
                            kind="blank"
                            label="More actions"
                            iconOnly
                            leadingIcon={Icon.MoreVertical}
                            palette={() =>
                                createGlossaryMorePalette({
                                    entries,
                                    displayedLanguages,
                                })
                            }
                        />
                        <GlossaryLanguagesButton
                            displayedLanguages={displayedLanguages}
                            actions={actions}
                            mode="select"
                        />
                        {hasChanges ? (
                            <Button
                                label="Cancel"
                                kind="secondary"
                                title="Discard changes made to the glossary"
                                onPress={() => actions.discardChanges()}
                            />
                        ) : null}
                        {hasChanges && editable ? (
                            <Button
                                label="Save"
                                title="Save changes made to the glossary"
                                onPress={() =>
                                    void confirm({
                                        title: 'Save glossary and re-run translations?',
                                        description: (
                                            <>
                                                Saving the glossary will re-run translations for all
                                                pages. This increases the number of translated words
                                                and may impact usage billing. We recommend batching
                                                multiple changes together.
                                            </>
                                        ),
                                        confirmLabel: 'Save changes',
                                        onConfirm: actions.saveChanges,
                                    })
                                }
                            />
                        ) : null}
                    </ButtonGroup>
                }
            >
                <DialogRoot {...importDialog.rootProps}>
                    <Dialog>
                        <GlossaryImportDialog actions={actions} />
                    </Dialog>
                </DialogRoot>
                {!editable && entries.length === 0 ? (
                    <EmptyState>
                        <EmptyStateIcon>
                            <Icon.AITranslate />
                        </EmptyStateIcon>
                        <Heading>No glossary entries</Heading>
                        <Description>No entries have been added to the glossary yet.</Description>
                    </EmptyState>
                ) : (
                    <>
                        <div className="w-full shrink grow-0 overflow-auto overscroll-contain pb-1">
                            <div className="-m-px table w-max border-collapse">
                                <GlossaryTableHeader
                                    displayedLanguages={displayedLanguages}
                                    actions={actions}
                                />
                                <div className="table-row-group">
                                    {entries.map((entry) => (
                                        <GlossaryEntryRow
                                            key={entry.entryId}
                                            entry={entry}
                                            editable={editable}
                                            actions={actions}
                                            displayedLanguages={displayedLanguages}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-2 p-3">
                            {actions.fetchMoreEntries ? (
                                <Button
                                    label="Load more"
                                    leadingIcon={Icon.ArrowDown}
                                    kind="blank"
                                    onPress={() => actions.fetchMoreEntries?.()}
                                />
                            ) : null}
                            {editable ? (
                                <Button
                                    leadingIcon={Icon.Plus}
                                    label="Add glossary entry"
                                    kind="blank"
                                    onPress={() => {
                                        actions.insertGlossaryEntries([{}]);
                                    }}
                                />
                            ) : null}
                            {editable ? (
                                <Button
                                    leadingIcon={Icon.Upload}
                                    label="Upload glossary"
                                    tooltip={{
                                        label: 'Import an existing glossary from a CSV file',
                                    }}
                                    kind="blank"
                                    onPress={() => importDialog.setOpen(true)}
                                />
                            ) : null}
                        </div>
                    </>
                )}
            </TranslationsScreenLayout>
        </>
    );
}
